import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { Observable, of, Subscription } from 'rxjs';
import { filter, tap, map } from 'rxjs/operators';

import { ChatMessageTypeModel } from '@api/ai/enums/AI-chat-completion.enum';
import { AbstractBaseStyleFieldComponent } from '@Common/PageBuilder/components/BaseStyleField/abstract-base-style-field.component';
import { AbstractPageBuilderService } from '@Common/PageBuilder/services/abstract-page-builder.service';
import { ButtonTypesEnum } from '@Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { MentionInsightTypes } from '@Common/Mentions/enums/mention-insight-types.enum';
import { mentionInsightTypesOptions } from '@Common/Forms/data/mention-insight-types-options';
import { promptLanguageOptions } from '@Common/Forms/data/prompt-language-options';
import { DropdownOptionModel } from '@Common/Forms/models/dropdown-option.model';
import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
import { FullProfileAnimationModel } from '@Core/Dialogs/models/full-profile-animation.model';
import { AutomaticAdditionalSettingsField } from '../../models/automatic-additional-settings-field.model';
import { BriefingsService } from '../../services/briefings.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { CustomInsights } from '@Monitor/Shared/models/custom-insights.model';
import { CoverageAnalysisService } from '@Monitor/Shared/services/coverage-analysis.service';
import { TextInputDialogModel } from '@Common/Dialogs/models/text-input-dialog.model';
import { TextInputDialogComponent } from '@Common/Dialogs/components/TextInputDialog/text-input-dialog.component';
import { CustomInsightType } from '@Monitor/Shared/models/custom-insights-type.model';
import { userPermissionsEnum } from '@Core/Users/enums/user-permissions.enum';
import { AdditionalSettings } from '../../models/additional-settings.model';
import { KeywordSelectionEnum } from '@Monitor/Shared/enums/precision-keyword-selection.enum';

@Component({
    selector: 'agpr-automatic-additional-settings-field',
    templateUrl: './automatic-additional-settings-field.component.html',
    styleUrls: ['./automatic-additional-settings-field.component.scss'],
    standalone: false
})
export class AutomaticAdditionalSettingsFieldComponent extends AbstractBaseStyleFieldComponent implements OnInit, OnDestroy {

    @Input() declare value: AdditionalSettings;
    @Input() declare field: AutomaticAdditionalSettingsField;
    @Input() id: string = null;

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    @Input() briefingId: number;

    isMentionKeywords: boolean = true;
    showInsights: boolean = false;
    confirmGenerate: boolean = false;
    withoutInsightsOnly: boolean = true;
    setPreferred: boolean = true;
    isSavedSource: boolean = false;

    manualKeywords: string[] = [];

    selectedMentionInsightType: DropdownOptionModel<MentionInsightTypes>;

    selectedPromptLanguageOption: DropdownOptionModel;

    allowCustomInsightSave: boolean = false;
    saveCustomInsightTooltip: string = 'Mention.workbench.insights.prompt.custom.prompt.save.tooltip';
    saveAsCustomInsightTooltip: string = 'Mention.workbench.insights.prompt.custom.prompt.saveAs.tooltip';

    customInsightsOptions: DropdownOptionModel<CustomInsights>[];
    selectedCustomInsightOption: DropdownOptionModel<CustomInsights>;

    customInsightCopy: CustomInsights;


    /** Precision Clip Variables */

    hasPrecisionClipPermission: boolean = false;
    generatePrecisionClip: boolean = false;

    keywordSelection: KeywordSelectionEnum = KeywordSelectionEnum.SAVED_MENTION;

    precisionManualKeywords: string[] = [];

    fallbackKeywords: string[] = [];

    insightsEnabled: boolean = true;

    extendStartTime: number = 5;
    extendedEndTime: number = 5;

    private subscriptions: Subscription[] = [];

    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly mentionInsightTypes = MentionInsightTypes;
    readonly mentionInsightTypesOptions = mentionInsightTypesOptions;
    readonly promptLanguageOptions = promptLanguageOptions;
    readonly keywordSelectionEnum = KeywordSelectionEnum;

    constructor(
        protected coreDialogService: CoreDialogService,
        protected briefingsService: AbstractPageBuilderService,
        private currentUserStateService: CurrentUserStateService,
        private coverageAnalysisService: CoverageAnalysisService
    ) {
        super(coreDialogService);
    }

    ngOnInit() {
        if (this.value?.autoGenInsight) {
            this.confirmGenerate = true;
            if (this.value.autoGenInsight.customKeywords?.length) {
                this.isMentionKeywords = false;
                this.manualKeywords = this.value.autoGenInsight.customKeywords;
            }

            if (this.value.autoGenInsight.language) {
                const lang = this.promptLanguageOptions.find(option => option.value === this.value.autoGenInsight.language);
                if (lang) {
                    this.selectedPromptLanguageOption = lang;
                }
            }

            if (this.value.autoGenInsight.completionFormat) {
                const insightType = this.mentionInsightTypesOptions.find(option => {
                    return `${ChatMessageTypeModel.MENTION_INSIGHTS}_${option.value}` === this.value.autoGenInsight.completionFormat;
                });
                if (insightType) {
                    this.selectedMentionInsightType = insightType;
                }
            }

            this.setPreferred = this.value.autoGenInsight.preferred;
            this.withoutInsightsOnly = this.value.autoGenInsight.mentionWithoutInsightsOnly;
        } else {
            this.confirmGenerate = false;
        }

        if (this.value?.precisionClip) {
            this.generatePrecisionClip = !!this.value.precisionClip;
            this.keywordSelection = this.value.precisionClip.savedMentionKeywords ? KeywordSelectionEnum.SAVED_MENTION : KeywordSelectionEnum.MANUAL;
            this.precisionManualKeywords = this.value.precisionClip.manualKeywords;
            this.fallbackKeywords = this.value.precisionClip.fallbackKeywords;
            this.insightsEnabled = this.value.precisionClip.generateInsights;
            this.extendStartTime = this.value.precisionClip.extendedStart;
            this.extendedEndTime = this.value.precisionClip.extendedEnd;
        } else {
            this.generatePrecisionClip = false;
        }

        this.subscriptions.push(
            (this.briefingsService as BriefingsService).getCurrentAutoDataSource().subscribe(currentAutoDataSource => {
                this.isSavedSource = currentAutoDataSource?.isSavedSource();
            }),
            (this.briefingsService as BriefingsService).getUpdatedAutomaticBlock().subscribe((currentAutomaticBlock) => {
                this.showInsights = currentAutomaticBlock?.displayElement?.styles.showInsights;
            }),
            this.currentUserStateService.getUILanguageTag().subscribe(tag => {
                this.promptLanguageOptions.forEach(option => option.label = `${tag}.${option.value}`);
            }),
            this.coverageAnalysisService.customInsights$.pipe(
                map(x => x.filter(item => item.promptType === CustomInsightType.MENTION_INSIGHTS)),
            ).subscribe(customInsights => {
                this.customInsightsOptions = [];
                const newCustomPrompt = new CustomInsights();
                newCustomPrompt.promptType = CustomInsightType.MENTION_INSIGHTS;

                this.customInsightsOptions.push(new DropdownOptionModel('Briefings.analysis.customInsight.new', newCustomPrompt));
                if (customInsights?.length) {
                    for (const customInsight of customInsights) {
                        this.customInsightsOptions.push(new DropdownOptionModel(customInsight.name, customInsight.clone()));
                    }
                    if (this.value?.autoGenInsight?.customPromptId) {
                        // eslint-disable-next-line max-len
                        this.selectedCustomInsightOption = this.customInsightsOptions.find(insight => insight.value.id === this.value.autoGenInsight.customPromptId);
                    }
                }
            }),
            this.currentUserStateService.getPermissions().pipe(filter(x => !!x)).subscribe(
                permissions => {
                    this.hasPrecisionClipPermission = permissions?.includes(userPermissionsEnum.precisionClipper);
                }
            )
        );
    }

    setInsightsDefault() {
        if (this.confirmGenerate) {
            this.isMentionKeywords = true;
            this.manualKeywords = [];
            this.selectedPromptLanguageOption = this.promptLanguageOptions.find(option => option.value === 'en-US');
            this.selectedMentionInsightType = this.mentionInsightTypesOptions.find(option => option.value === MentionInsightTypes.CONTEXTUAL_ANALYSIS);
            this.setPreferred = false;
            this.withoutInsightsOnly = false;
        }
        this.updateValue();
    }

    setPrecisionDefault() {
        if (this.generatePrecisionClip) {
            this.keywordSelection = KeywordSelectionEnum.SAVED_MENTION;
            this.fallbackKeywords = [];
            this.precisionManualKeywords = [];
            this.insightsEnabled = true;
            this.extendStartTime = 5;
            this.extendedEndTime = 5;
        }
        this.updateValue();
    }


    /**
     * Update `this.value` property with form variables
     */
    updateValue() {
        this.value = {
            autoGenInsight: {},
            precisionClip: {}
        };

        if (this.confirmGenerate) {
            this.value.autoGenInsight.completionFormat = `${ChatMessageTypeModel.MENTION_INSIGHTS}_${this.selectedMentionInsightType.value}`;

            if (this.isSavedSource && this.withoutInsightsOnly) {
                this.value.autoGenInsight.mentionWithoutInsightsOnly = this.withoutInsightsOnly;
            } else {
                this.value.autoGenInsight.mentionWithoutInsightsOnly = false;
            }
            if (this.isSavedSource && this.setPreferred) {
                this.value.autoGenInsight.preferred = this.setPreferred;
            } else {
                this.value.autoGenInsight.preferred = false;
            }
            if (this.selectedMentionInsightType.value !== MentionInsightTypes.QUOTES) {
                this.value.autoGenInsight.language = this.selectedPromptLanguageOption.value;
            }
            if (!this.isMentionKeywords && this.manualKeywords?.length) {
                this.value.autoGenInsight.customKeywords = this.manualKeywords;
            }
        } else {
            this.value.autoGenInsight = null;
        }

        if (this.generatePrecisionClip) {
            this.value.precisionClip.savedMentionKeywords = this.keywordSelection === KeywordSelectionEnum.SAVED_MENTION;
            this.value.precisionClip.fallbackKeywords = this.fallbackKeywords;
            this.value.precisionClip.manualKeywords = this.precisionManualKeywords;
            this.value.precisionClip.generateInsights = this.insightsEnabled;
            this.value.precisionClip.extendedStart = this.extendStartTime;
            this.value.precisionClip.extendedEnd = this.extendedEndTime;
        } else {
            this.value.precisionClip = null;
        }

        this.changes.emit(this.value);
    }

    showInsightChange() {
        this.block.displayElement.styles.showInsights = this.showInsights;
        this.changes.emit(this.value);
        (this.briefingsService as BriefingsService).isBoundToGlobalStylesChange(this.block, '', true);
    }

    selectCustomPrompt(option: DropdownOptionModel): void {
        this.value.autoGenInsight.customPromptId = option?.value?.id;

        if (this.selectedCustomInsightOption.value?.id) {
            this.customInsightCopy = this.selectedCustomInsightOption.value.clone();
            if (this.block?.errors) {
                this.block.errors = this.block.errors.filter(error => !error.includes('Briefings.analysis.errors.customPromptIsRequired'));
            }
        } else {
            this.customInsightCopy = null;
        }

        this.customInsightFieldChanged();
        this.changes.emit(this.value);
    }

    saveCustomInsight() {
        if (this.selectedCustomInsightOption.value?.id) {
            this.coverageAnalysisService.updateCustomInsight(this.selectedCustomInsightOption.value).subscribe(success => {
                if (success) {
                    this.customInsightCopy = this.selectedCustomInsightOption.value.clone();
                    this.allowCustomInsightSave = false;
                }
            });
        } else {
            this.createInsight().subscribe();
        }
    }

    openSaveAsInsightDialog() {
        const io: TextInputDialogModel = {
            inputs: {
                id: 'nameCustomInsight',
                heading: 'Briefings.analysis.customInsight.header',
                okLabel: 'Briefings.analysis.customAnalysis.save',
                placeholder: 'Briefings.analysis.customAnalysisName.placeholder',
                description: 'Briefings.analysis.customInsight.label',
                initialValue: this.selectedCustomInsightOption.value.name,
                submit: (name: string) => {
                    this.selectedCustomInsightOption.value.name = name;
                    return this.createInsight();
                }
            }
        };
        this.coreDialogService.open(TextInputDialogComponent, io);
    }

    customInsightFieldChanged() {
        if (this.selectedCustomInsightOption.value?.id) {
            this.allowCustomInsightSave = !this.selectedCustomInsightOption.value.equals(this.customInsightCopy);
        } else {
            this.allowCustomInsightSave = true;
        }

        if (!this.selectedCustomInsightOption.value.name?.trim()) {
            this.allowCustomInsightSave = false;
            this.saveCustomInsightTooltip = 'Mention.workbench.insights.prompt.custom.prompt.name.required';
            this.saveAsCustomInsightTooltip = 'Mention.workbench.insights.prompt.custom.prompt.name.required';

            return;
        }

        if (!this.selectedCustomInsightOption.value.prompt?.trim()) {
            this.allowCustomInsightSave = false;
            this.saveCustomInsightTooltip = 'Mention.workbench.insights.prompt.custom.prompt.required';
            this.saveAsCustomInsightTooltip = 'Mention.workbench.insights.prompt.custom.prompt.required';

            return;
        }

        this.saveCustomInsightTooltip = 'Mention.workbench.insights.prompt.custom.prompt.save.tooltip';
        this.saveAsCustomInsightTooltip = 'Mention.workbench.insights.prompt.custom.prompt.saveAs.tooltip';
    }


    private createInsight(): Observable<number> {
        return this.coverageAnalysisService.createCustomInsight(this.selectedCustomInsightOption.value.clone()).pipe(
            tap(id => {
                if (id) {
                    this.value.autoGenInsight.customPromptId = id;
                    this.allowCustomInsightSave = false;
                }
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
